import { InputNumber, Radio } from 'antd';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { PresetFormValues } from '../../../lib/types';
import { PREVIEW_MODES } from '@modules/presets/lib/constants';
import { DynamicStringList } from '../dynamic-string-list';
import { PresetFormItem } from '../form-item';
import styles from '../preset-form.module.scss';

interface PreviewSettingsProps {
  control: Control<PresetFormValues>;
  errors: FieldErrors<PresetFormValues>;
  disabled?: boolean;
}

export const PreviewSettings = ({ control, errors, disabled }: PreviewSettingsProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'presets.editor.previewSettings' });
  const mode = useWatch({ control, name: 'preview.mode' });

  return (
    <div className={styles.fieldsGrid}>
      <PresetFormItem label={t('mode')} fullWidth required>
        <Controller
          control={control}
          name="preview.mode"
          render={({ field }) => (
            <Radio.Group
              {...field}
              disabled={disabled}
              optionType="button"
              buttonStyle="solid"
              options={PREVIEW_MODES.map((previewMode) => ({
                label: t(`modes.${previewMode}`),
                value: previewMode,
              }))}
              className={styles.segmentedControl}
            />
          )}
        />
      </PresetFormItem>

      <PresetFormItem
        label={t('delay')}
        error={errors.preview?.show_delay_seconds?.message}
        required
      >
        <Controller
          control={control}
          name="preview.show_delay_seconds"
          render={({ field }) => (
            <InputNumber
              {...field}
              min={0}
              max={3600}
              disabled={disabled}
              placeholder={t('delayPlaceholder')}
              addonAfter={t('seconds')}
              className={styles.numberInput}
            />
          )}
        />
      </PresetFormItem>

      {mode === 'static' && (
        <PresetFormItem label={t('staticAnswers')} fullWidth>
          <DynamicStringList
            control={control}
            name="preview.static_answers"
            placeholder={t('staticAnswerPlaceholder')}
            addLabel={t('addStaticAnswer')}
            disabled={disabled}
          />
        </PresetFormItem>
      )}
    </div>
  );
};
